# Route: /admin/tenants/:tenantId/invitations
# SystemAdmin "add a Tenant Admin" surface for ONE managed tenant: the invitation
# list and the send action carry ?tenantId= so the API operates on the SELECTED
# tenant (SystemAdmin-gated server-side via CrossTenantScope), not the SA's own
# SYSTEM tenant. There is no role picker — a cross-tenant invite is always a
# TenantAdmin (fixed server-side in InvitationEndpoints). The new admin then
# staffs their own tenant from the in-tenant /admin/invitations page, which is
# where role selection lives (pages/admin/invitation-list.cir.yaml).

page-admin-tenant-invitations:
  type: Page
  archetype: detail
  meta:
    title: $t.admin.invitations.tenant_meta_title
    auth: required
  params:
    tenantId:
      type: string
      required: true
  data:
    tenant:
      model: TenantResponse
      key: $route.tenantId
      refresh: on_mount
    invitations:
      model: InvitationResponse[]
      # /api/invitations is gated by .WithRequiresFilter("status", "email",
      # "tenantId"). The route key is sent as the tenantId filter, and `filter:`
      # seeds _queryState so the mount-time fetch also carries status; the
      # operator can widen via the table header.
      key: $route.tenantId
      filter:
        status: Pending
      refresh: on_mount
    form:
      model: CreateInvitationForm
      initial:
        email: ""
        firstName: ""
        lastName: ""
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - tenant-invitations-header
    - tenant-invitations-tenant-context
    - tenant-invitation-send-form
    - tenant-invitation-table

# ── Header — static localized title; the managed tenant's name is shown as a
# context subheading below (detail-archetype convention: a static PageHeader
# title plus a separate bound identity node — see tenant-detail.cir.yaml). ──

tenant-invitations-header:
  type: PageHeader
  title: $t.admin.invitations.tenant_title
  back_route: /admin/tenants

# Context line identifying which managed tenant the SystemAdmin is staffing.
tenant-invitations-tenant-context:
  type: Text
  bind:
    content: tenant.name
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    color: $color_scheme.light.color.on-surface-muted

# ── Add-administrator form (targets the managed tenant) ─────────────────────
# A SystemAdmin can add only a Tenant Admin here — the server fixes the role to
# TenantAdmin for every cross-tenant (?tenantId=) invite, so there is no role
# picker. The new admin then staffs their own tenant (operators, supervisors,
# QA) from the in-tenant /admin/invitations page.
# Titled-section card pattern (my-tenant-profile.cir.yaml) — see
# invitation-list.cir.yaml's mirror-image comment (F78c Act 1.6).

tenant-invitation-send-form:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - tenant-invitation-send-form-title
    - tenant-invitation-send-form-hint
    - tenant-invitation-send-fields

tenant-invitation-send-form-title:
  type: Text
  content: $t.admin.invitations.tenant_send_form_heading
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.md
    font_weight: "600"
    color: $color_scheme.light.color.on-surface

tenant-invitation-send-form-hint:
  type: Text
  content: $t.admin.invitations.tenant_send_form_hint
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-variant

# Three lines, in tab order: the email; the two name parts; Send. The row wraps and
# the name row claims a whole line (`flex: 1 1 100%`), so the email gets the full
# width a long address needs. Lines align `flex-start`: a field keeps its place when
# an error or a helper appears under it or under its neighbour.
tenant-invitation-send-fields:
  type: Section
  spatial:
    display: flex
    wrap: wrap
    gap: $spacing.sm
    align: flex-start
    responsive:
      sm:
        direction: column
        align: stretch
  children:
    - tenant-invitation-email-input
    - tenant-invitation-name-fields
    - tenant-invitation-send-btn

tenant-invitation-name-fields:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
    align: flex-start
    flex: 1 1 100%
    responsive:
      sm:
        direction: column
        align: stretch
  children:
    - tenant-invitation-first-name-input
    - tenant-invitation-last-name-input

tenant-invitation-email-input:
  type: Input
  input_type: email
  label: $t.admin.invitations.field_email
  bind: form.email
  a11y:
    label: $t.admin.invitations.field_email_label
  validation:
    required: true
  states:
    loading:
      opacity: 0.6
  spatial:
    flex: 1
    min_height: $size.touch-target

# The invited administrator's name: the directory user the invitation creates
# carries a first and a last name, 1..255 characters each, and the server
# refuses a send without them.
tenant-invitation-first-name-input:
  type: Input
  input_type: text
  # `model:` lets the emitter resolve the bound field of an Input that sits outside a
  # Form, which is what puts the field's `maxlength` on the element.
  model: CreateInvitationForm
  label: $t.admin.invitations.field_first_name
  helper: $t.admin.invitations.name_hint
  bind: form.firstName
  a11y:
    label: $t.admin.invitations.field_first_name_label
  # Mirrors PersonName (Xp87.Domain): at least one letter, and none of the characters
  # Keycloak's person-name rule prohibits — < > & " $ % ! # ? § ; * ~ / \ | ^ = [ ] { } ( ) —
  # nor a control character, a line or paragraph separator, or a text-direction control
  # (U+061C, U+200E, U+200F, U+202A to U+202E, U+2066 to U+2069; the two hyphens in the
  # class are ranges). The zero-width joiner and non-joiner stay allowed. Every punctuation
  # mark is escaped, and the four HTML-significant ones are written as \xNN: the browser
  # compiles `pattern` with the `v` flag and ignores one that fails there (XP85043). The
  # 255-character bound is the bound model field's `max_length`, which emits `maxlength`.
  validation:
    required: true
    pattern: '^(?=.*\p{L})[^\x22\x26\x3C\x3E\$\%\!\#\?§\;\*\~\/\\\|\^\=\[\]\{\}\(\)\p{Cc}\u2028\u2029\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]+$'
    messages:
      required: $t.admin.invitations.first_name_required
      pattern: $t.admin.invitations.name_invalid
  states:
    loading:
      opacity: 0.6
  spatial:
    flex: 1
    min_height: $size.touch-target

tenant-invitation-last-name-input:
  type: Input
  input_type: text
  # `model:` lets the emitter resolve the bound field of an Input that sits outside a
  # Form, which is what puts the field's `maxlength` on the element.
  model: CreateInvitationForm
  label: $t.admin.invitations.field_last_name
  bind: form.lastName
  a11y:
    label: $t.admin.invitations.field_last_name_label
  # Mirrors PersonName (Xp87.Domain): at least one letter, and none of the characters
  # Keycloak's person-name rule prohibits — < > & " $ % ! # ? § ; * ~ / \ | ^ = [ ] { } ( ) —
  # nor a control character, a line or paragraph separator, or a text-direction control
  # (U+061C, U+200E, U+200F, U+202A to U+202E, U+2066 to U+2069; the two hyphens in the
  # class are ranges). The zero-width joiner and non-joiner stay allowed. Every punctuation
  # mark is escaped, and the four HTML-significant ones are written as \xNN: the browser
  # compiles `pattern` with the `v` flag and ignores one that fails there (XP85043). The
  # 255-character bound is the bound model field's `max_length`, which emits `maxlength`.
  validation:
    required: true
    pattern: '^(?=.*\p{L})[^\x22\x26\x3C\x3E\$\%\!\#\?§\;\*\~\/\\\|\^\=\[\]\{\}\(\)\p{Cc}\u2028\u2029\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]+$'
    messages:
      required: $t.admin.invitations.last_name_required
      pattern: $t.admin.invitations.name_invalid
  states:
    loading:
      opacity: 0.6
  spatial:
    flex: 1
    min_height: $size.touch-target

# POST carries ?tenantId= — CrossTenantScope verifies the SystemAdmin gate and
# the invitation lands in the managed tenant as a TenantAdmin (role fixed
# server-side; the form posts the email and the name, never a role).
tenant-invitation-send-btn:
  type: Button
  color: info
  content: $t.admin.invitations.send
  a11y:
    label: $t.admin.invitations.send_label
  action:
    type: custom
    method: POST
    url: /api/invitations?tenantId=:tenantId
    body: form
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg
  tokens:
    radius: $radius.md
  states:
    disabled:
      opacity: 0.4
    hover:
      background: $color_scheme.light.color.info-text
      motion:
        transition: $motion.ease.out.150
        reduced: instant

# ── Invitation table ─────────────────────────────────────────────────────────

tenant-invitation-table:
  type: Table
  bind:
    rows: invitations
  empty: tenant-invitation-empty
  a11y:
    label: $t.admin.invitations.table_label
  columns:
    - key: email
      label: $t.admin.invitations.col_email
      width: 2fr
    - key: status
      label: $t.admin.invitations.col_status
      width: 1fr
    - key: createdAt
      label: $t.admin.invitations.col_sent
      format: date-relative
      width: 1fr
    - key: expiresAt
      label: $t.admin.invitations.col_expires
      format: date
      width: 1fr

tenant-invitation-empty:
  type: EmptyState
  content: $t.admin.invitations.tenant_empty
  body: $t.admin.invitations.tenant_empty_body
